import { Autocomplete, Chip, type SxProps, TextField, type Theme } from "@mui/material";
import { type FC, useCallback, useId, useState } from "react";

/**
 * Commits the field's entries and returns whatever it could not read, for the field to name.
 * Returning `[]` means everything landed.
 */
export type TokenListCommit = (entries: string[]) => string[];

/**
 * Short free-text values as removable chips, suggesting values already used elsewhere.
 *
 * Two behaviours are load-bearing (DAT-728): `onBlur` commits pending text, because MUI's `freeSolo`
 * only chips on Enter and leaves typed text looking saved when it isn't; and `onCommit` returns what
 * it could not parse so the helper text names it, rather than dropping it silently.
 *
 * The caller owns parsing and storage. Raw MUI `Autocomplete`, not `SystemAutoComplete` — that
 * wrapper is single-select by construction and cannot express a multi-value free-solo field.
 */
export const TokenListField: FC<{
	/** Chips currently configured, already formatted for display. */
	values: string[];
	/** Values configured elsewhere, offered as a dropdown so a field can reuse them. */
	options: string[];
	/** Called with the full next set of entries; returns the ones it could not read. */
	onCommit: TokenListCommit;
	/** Already-translated message naming the rejected entries. */
	invalidMessage: (rejected: string[]) => string;
	/** Already-translated hint shown while the field is empty. */
	placeholder?: string;
	disabled?: boolean;
	/** Forwarded to the inner `TextField` — the settings tables pass their borderless inline-editor sx. */
	fieldSx?: SxProps<Theme>;
	sx?: SxProps<Theme>;
	/** Stamped on the `<input>`, so a test can target one field without depending on render order. */
	dataTestId?: string;
}> = ({ values, options, onCommit, invalidMessage, placeholder, disabled = false, fieldSx, sx, dataTestId }) => {
	const inputId = useId();
	const [inputValue, setInputValue] = useState("");
	const [rejected, setRejected] = useState<string[]>([]);

	const handleInputChange = useCallback(
		(next: string) => {
			setInputValue(next);
			// Typing again is the user fixing the entry; keep the complaint up only until then.
			if (next && rejected.length > 0) setRejected([]);
		},
		[rejected.length],
	);

	return (
		<Autocomplete
			id={inputId}
			freeSolo
			multiple
			disabled={disabled}
			filterSelectedOptions
			options={options}
			value={values}
			inputValue={inputValue}
			sx={sx}
			onInputChange={(_event, next) => handleInputChange(next)}
			onBlur={() => {
				if (!inputValue.trim()) return;
				setRejected(onCommit([...values, inputValue]));
				setInputValue("");
			}}
			onChange={(_event, next) => setRejected(onCommit(next as string[]))}
			renderInput={(params) => (
				<TextField
					{...params}
					size="small"
					sx={fieldSx}
					error={rejected.length > 0}
					helperText={rejected.length > 0 ? invalidMessage(rejected) : undefined}
					// `params.slotProps` must be SPREAD, not replaced. It carries the `input` slot whose
					// `startAdornment` IS the chip list (and whose `endAdornment` is the clear button);
					// passing a bare `{ htmlInput }` dropped both, so a field with values rendered as an
					// empty box — the configured lengths were stored and saved, just never drawn.
					slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps?.htmlInput, "data-testid": dataTestId } }}
					placeholder={values.length === 0 ? placeholder : undefined}
				/>
			)}
			renderValue={(value, getTagProps) =>
				value.map((label, index) => {
					const { key, ...tagProps } = getTagProps({ index });
					return <Chip variant="outlined" label={label} key={key} {...tagProps} size="small" />;
				})
			}
		/>
	);
};

export default TokenListField;
